import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Plus, Filter, SlidersHorizontal, Scan, Calendar, ListChecks, CheckSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useContacts } from "@/hooks/useContacts";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useMergeContacts } from "@/hooks/useMergeContacts";
import { useSalesDevelopment } from "@/hooks/useSalesDevelopment";
import { useUserLanguage } from "@/hooks/useUserLanguage";
import { useScheduledCallsByContact } from "@/hooks/useScheduledCallsByContact";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { SOURCE_TYPES, CHANNEL_TYPES } from "@/lib/sourceChannelTypes";
import { BUSINESS_UNITS } from "@/lib/businessUnits";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";

import ContactForm from "../components/contacts/ContactForm";
import ContactTable from "../components/contacts/ContactTable";
import { MergeDuplicatesDialog } from "@/components/contacts/MergeDuplicatesDialog";

import { PrimaryButton } from "@/components/common/PrimaryButton";
import { ActiveFilters } from "@/components/common/ActiveFilters";
import BulkActionBar from "@/components/common/BulkActionBar";
import ListModal from "@/components/lists/ListModal";
import { User } from "@/api/entities";

const DEAL_STAGES = [
  { value: 'new', labelKey: 'stages.new' },
  { value: 'contact', labelKey: 'stages.contact' },
  { value: 'scheduled', labelKey: 'stages.scheduled' },
  { value: 'proposal', labelKey: 'stages.proposal' },
  { value: 'negotiation', labelKey: 'stages.negotiation' },
  { value: 'won', labelKey: 'stages.closedWon' },
  { value: 'lost', labelKey: 'stages.lost' },
];

const CONTACT_TYPES = [
  { value: 'prospect', labelKey: 'contacts.prospect' },
  { value: 'active_customer', labelKey: 'contacts.activeCustomer' },
  { value: 'past_customer', labelKey: 'contacts.pastCustomer' },
];

const VERIFICATION_STATUSES = [
  { value: 'unverified', labelKey: 'verification.unverified' },
  { value: 'verified', labelKey: 'verification.verified' },
  { value: 'invalid', labelKey: 'verification.invalid' },
];

const CREATED_DATE_OPTIONS = [
  { value: 'today', labelKey: 'dates.today' },
  { value: 'yesterday', labelKey: 'dates.yesterday' },
  { value: 'this_week', labelKey: 'dates.thisWeek' },
  { value: 'this_month', labelKey: 'dates.thisMonth' },
  { value: 'custom', labelKey: 'dates.customPeriod' },
];

const BUSINESS_UNIT_OPTIONS = [
  { value: 'unassigned', labelKey: 'common.unassigned' },
  ...BUSINESS_UNITS.map(bu => ({
    value: bu.value,
    label: `${bu.flag} ${bu.label}`
  }))
];

const REACTIVATION_STATUS_OPTIONS = [
  { value: 'scheduled', labelKey: 'reactivation.scheduled' },
  { value: 'in_progress', labelKey: 'reactivation.inProgress' },
  { value: 'completed', labelKey: 'reactivation.completed' },
  { value: 'no_response', labelKey: 'reactivation.noResponse' },
];

export default function Contacts() {
  const { t } = useTranslation();
  const { dateLocale, dateFormats } = useUserLanguage();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterNoDeals, setFilterNoDeals] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  // Multi-select filter states (arrays)
  const [salesConsultantFilter, setSalesConsultantFilter] = useState([]);
  const [sdrFilter, setSdrFilter] = useState([]);
  const [verificationFilter, setVerificationFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [channelFilter, setChannelFilter] = useState([]);
  const [contactTypeFilter, setContactTypeFilter] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState([]);
  const [dealStageFilter, setDealStageFilter] = useState([]);
  const [createdDateFilter, setCreatedDateFilter] = useState([]);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [businessUnitFilter, setBusinessUnitFilter] = useState([]);
  const [reactivationStatusFilter, setReactivationStatusFilter] = useState([]);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Bulk selection for lists
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [showListModal, setShowListModal] = useState(false);

  const toggleSelectContact = (contactId) => {
    setSelectedContactIds(prev =>
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };

  const clearContactSelection = () => setSelectedContactIds([]);

  // Memoize processed filter arrays to prevent infinite re-fetching
  const processedSalesConsultantFilter = useMemo(() => {
    if (salesConsultantFilter.includes('me') && currentUser?.id) {
      return [...salesConsultantFilter.filter(v => v !== 'me'), currentUser.id];
    }
    return salesConsultantFilter;
  }, [salesConsultantFilter, currentUser?.id]);

  const processedSdrFilter = useMemo(() => {
    if (sdrFilter.includes('me') && currentUser?.id) {
      return [...sdrFilter.filter(v => v !== 'me'), currentUser.id];
    }
    return sdrFilter;
  }, [sdrFilter, currentUser?.id]);

  const processedCreatedByFilter = useMemo(() => {
    if (createdByFilter.includes('me') && currentUser?.id) {
      return [...createdByFilter.filter(v => v !== 'me'), currentUser.id];
    }
    return createdByFilter;
  }, [createdByFilter, currentUser?.id]);

  const processedDealStageFilter = useMemo(() => {
    return dealStageFilter.filter(v => v !== 'no_deals');
  }, [dealStageFilter]);

  const { 
    contacts, 
    totalCount,
    loading: isLoading, 
    loadingMore,
    backgroundProgress,
    hasMore,
    loadMore,
    createContact, 
    loadContacts 
  } = useContacts(
    filterNoDeals || dealStageFilter.includes('no_deals'), 
    { 
      page: 1, 
      limit: 200,
      searchQuery: debouncedSearch,
      salesConsultantFilter: processedSalesConsultantFilter,
      sdrFilter: processedSdrFilter,
      verificationFilter,
      enabled: preferencesLoaded,
      isKanbanMode: false,
      sourceFilter,
      channelFilter,
      contactTypeFilter,
      createdByFilter: processedCreatedByFilter,
      dealStageFilter: processedDealStageFilter,
      createdDateFilter,
      customDateRange,
      businessUnitFilter,
      reactivationStatusFilter,
    }
  );
  const { users: appUsers } = useAppUsers();
  const { salesDevelopment: sdrs } = useSalesDevelopment();
  const { loading: mergeLoading, duplicates, findDuplicates, mergeContacts } = useMergeContacts();
  const { scheduledCallsByContact } = useScheduledCallsByContact();
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    contactInfo: true,
    location: true,
    contactType: false,
    verificationStatus: false,
    created: true
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);


  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    setUserLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        setUserLoading(false);
        return;
      }
      
      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();
        
      if (error) throw error;
      setCurrentUser(data);
    } catch (error) {
      console.error('Failed to load current user:', error);
    } finally {
      setUserLoading(false);
    }
  };

  // Apply saved filters immediately when currentUser loads
  useEffect(() => {
    if (!currentUser) return;
    
    if (currentUser.metadata?.contacts?.filters) {
      const savedFilters = currentUser.metadata.contacts.filters;
      setFilterNoDeals(savedFilters.filterNoDeals || false);
      setSalesConsultantFilter(Array.isArray(savedFilters.salesConsultantFilter) ? savedFilters.salesConsultantFilter : []);
      setSdrFilter(Array.isArray(savedFilters.sdrFilter) ? savedFilters.sdrFilter : []);
      setVerificationFilter(Array.isArray(savedFilters.verificationFilter) ? savedFilters.verificationFilter : []);
      setSourceFilter(Array.isArray(savedFilters.sourceFilter) ? savedFilters.sourceFilter : []);
      setChannelFilter(Array.isArray(savedFilters.channelFilter) ? savedFilters.channelFilter : []);
      setContactTypeFilter(Array.isArray(savedFilters.contactTypeFilter) ? savedFilters.contactTypeFilter : []);
      setCreatedByFilter(Array.isArray(savedFilters.createdByFilter) ? savedFilters.createdByFilter : []);
      setDealStageFilter(Array.isArray(savedFilters.dealStageFilter) ? savedFilters.dealStageFilter : []);
      setCreatedDateFilter(Array.isArray(savedFilters.createdDateFilter) ? savedFilters.createdDateFilter : []);
      setBusinessUnitFilter(Array.isArray(savedFilters.businessUnitFilter) ? savedFilters.businessUnitFilter : []);
      setReactivationStatusFilter(Array.isArray(savedFilters.reactivationStatusFilter) ? savedFilters.reactivationStatusFilter : []);
      setVisibleColumns(savedFilters.visibleColumns || {
        name: true,
        contactInfo: true,
        location: true,
        contactType: false,
        verificationStatus: false,
        created: true
      });
      setSearchQuery(savedFilters.searchQuery || "");
    }
    setPreferencesLoaded(true);
  }, [currentUser?.id]);

  // Save filter preferences with debounce
  useEffect(() => {
    if (!currentUser?.id) return;
    
    const timer = setTimeout(async () => {
      const updatedSettings = {
        ...currentUser.metadata,
        contacts: {
          ...currentUser.metadata?.contacts,
          filters: {
            filterNoDeals,
            salesConsultantFilter,
            sdrFilter,
            verificationFilter,
            sourceFilter,
            channelFilter,
            contactTypeFilter,
            createdByFilter,
            dealStageFilter,
            createdDateFilter,
            businessUnitFilter,
            reactivationStatusFilter,
            visibleColumns,
            searchQuery
          }
        }
      };
      
      try {
        await User.updateMyUserData({ metadata: updatedSettings });
      } catch (error) {
        console.error("Error saving filter preferences:", error);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [filterNoDeals, salesConsultantFilter, sdrFilter, verificationFilter, sourceFilter, channelFilter, contactTypeFilter, createdByFilter, dealStageFilter, createdDateFilter, businessUnitFilter, reactivationStatusFilter, visibleColumns, searchQuery, currentUser?.id]);

  const handleCreateContact = async (contactData) => {
    setIsProcessing(true);
    try {
      await createContact(contactData);
      setShowForm(false);
      toast.success(t('messages.contactCreated') || "Contact created successfully!");
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error(error.message || t('messages.errorCreating'));
    }
    setIsProcessing(false);
  };

  const handleViewContact = (contactId) => {
    navigate(createPageUrl("ContactDetail") + "?id=" + contactId);
  };

  const handleToggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setFilterNoDeals(false);
    setSalesConsultantFilter([]);
    setSdrFilter([]);
    setVerificationFilter([]);
    setSourceFilter([]);
    setChannelFilter([]);
    setContactTypeFilter([]);
    setCreatedByFilter([]);
    setDealStageFilter([]);
    setCreatedDateFilter([]);
    setCustomDateRange(null);
    setBusinessUnitFilter([]);
    setReactivationStatusFilter([]);
  };

  const handleRemoveFilter = (filterKey, valueToRemove = null) => {
    if (filterKey === 'all') {
      handleClearAllFilters();
      toast.success(t('common.allFiltersRemoved'));
      return;
    }
    
    if (valueToRemove !== null) {
      switch (filterKey) {
        case 'salesConsultantFilter':
          setSalesConsultantFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'sdrFilter':
          setSdrFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'verificationFilter':
          setVerificationFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'sourceFilter':
          setSourceFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'channelFilter':
          setChannelFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'contactTypeFilter':
          setContactTypeFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'createdByFilter':
          setCreatedByFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'dealStageFilter':
          setDealStageFilter(prev => {
            const newFilter = prev.filter(v => v !== valueToRemove);
            if (valueToRemove === 'no_deals') setFilterNoDeals(false);
            return newFilter;
          });
          break;
        case 'createdDateFilter':
          setCreatedDateFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'businessUnitFilter':
          setBusinessUnitFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
        case 'reactivationStatusFilter':
          setReactivationStatusFilter(prev => prev.filter(v => v !== valueToRemove));
          break;
      }
      return;
    }
    
    switch (filterKey) {
      case 'searchQuery':
        setSearchQuery('');
        break;
      case 'filterNoDeals':
        setFilterNoDeals(false);
        break;
      case 'salesConsultantFilter':
        setSalesConsultantFilter([]);
        break;
      case 'sdrFilter':
        setSdrFilter([]);
        break;
      case 'verificationFilter':
        setVerificationFilter([]);
        break;
      case 'sourceFilter':
        setSourceFilter([]);
        break;
      case 'channelFilter':
        setChannelFilter([]);
        break;
      case 'contactTypeFilter':
        setContactTypeFilter([]);
        break;
      case 'createdByFilter':
        setCreatedByFilter([]);
        break;
      case 'dealStageFilter':
        setDealStageFilter([]);
        setFilterNoDeals(false);
        break;
      case 'createdDateFilter':
        setCreatedDateFilter([]);
        setCustomDateRange(null);
        break;
      case 'businessUnitFilter':
        setBusinessUnitFilter([]);
        break;
      case 'reactivationStatusFilter':
        setReactivationStatusFilter([]);
        break;
    }
  };

  const handleFindDuplicates = async () => {
    const found = await findDuplicates();
    if (found && found.length > 0) {
      setShowMergeDialog(true);
    } else {
      toast.success(t('contacts.noDuplicatesFound') || 'No duplicates found');
    }
  };

  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      clearContactSelection();
    }
    setSelectionMode(!selectionMode);
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 p-3 md:px-6 md:py-3 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 mb-2 items-start md:items-center">
            <div className="flex-1 w-full md:w-auto">
          <ActiveFilters 
            filters={{ 
              searchQuery, 
              filterNoDeals,
              salesConsultantFilter,
              sdrFilter,
              verificationFilter,
              sourceFilter,
              channelFilter,
              contactTypeFilter,
              createdByFilter,
              dealStageFilter,
              createdDateFilter,
              businessUnitFilter,
              reactivationStatusFilter,
            }} 
            onRemoveFilter={handleRemoveFilter}
            module="contacts"
            appUsers={appUsers}
            sdrs={sdrs}
          />
            </div>

            <div className="flex gap-2 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFindDuplicates}
                disabled={mergeLoading}
                className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-slate-200"
                title={t('contacts.findDuplicates') || "Find Duplicates"}
              >
                <Scan className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(true)}
                className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-white hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${showFilters ? 'bg-primary text-white hover:bg-primary/90' : 'hover:bg-slate-200'}`}
              >
                <Filter className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleSelectionMode}
                className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${selectionMode ? 'bg-primary text-white hover:bg-primary/90' : 'hover:bg-slate-200'}`}
                title="Modo de seleção"
              >
                <CheckSquare className="w-4 h-4" />
              </Button>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-slate-200"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm text-slate-900">Exibir Colunas</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-name"
                          checked={visibleColumns.name}
                          onCheckedChange={() => handleToggleColumn('name')}
                        />
                        <Label htmlFor="col-name" className="text-sm cursor-pointer">
                          Nome
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-contact"
                          checked={visibleColumns.contactInfo}
                          onCheckedChange={() => handleToggleColumn('contactInfo')}
                        />
                        <Label htmlFor="col-contact" className="text-sm cursor-pointer">
                          Informações de Contato
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-location"
                          checked={visibleColumns.location}
                          onCheckedChange={() => handleToggleColumn('location')}
                        />
                        <Label htmlFor="col-location" className="text-sm cursor-pointer">
                          Localização
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-contact-type"
                          checked={visibleColumns.contactType}
                          onCheckedChange={() => handleToggleColumn('contactType')}
                        />
                        <Label htmlFor="col-contact-type" className="text-sm cursor-pointer">
                          Tipo de Contato
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-verification-status"
                          checked={visibleColumns.verificationStatus}
                          onCheckedChange={() => handleToggleColumn('verificationStatus')}
                        />
                        <Label htmlFor="col-verification-status" className="text-sm cursor-pointer">
                          Status de Verificação
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="col-created"
                          checked={visibleColumns.created}
                          onCheckedChange={() => handleToggleColumn('created')}
                        />
                        <Label htmlFor="col-created" className="text-sm cursor-pointer">
                          Data de Criação
                        </Label>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-auto px-4 md:px-6 pb-4">
        <div className="max-w-7xl mx-auto">
          <ContactTable
            contacts={contacts}
            isLoading={isLoading}
            onViewContact={handleViewContact}
            visibleColumns={visibleColumns}
            selectedContactIds={selectionMode ? selectedContactIds : undefined}
            onToggleSelect={selectionMode ? toggleSelectContact : undefined}
            onSelectAll={selectionMode ? (checked) => {
              if (checked) {
                contacts.forEach(c => {
                  if (!selectedContactIds.includes(c.id)) toggleSelectContact(c.id);
                });
              } else {
                clearContactSelection();
              }
            } : undefined}
          />

          {!isLoading && contacts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">{t('contacts.noContactsFound') || 'No contacts found'}</p>
            </div>
          )}
        </div>

        {/* Load More Button */}
        {!isLoading && hasMore && contacts.length > 0 && (
          <div className="flex justify-center py-8">
            <Button
              onClick={loadMore}
              variant="outline"
              size="lg"
              className="rounded-full px-8"
            >
              {t('contacts.loadMore200') || 'Load more 200 contacts'}
            </Button>
          </div>
        )}

        {/* Total Counter */}
        {!isLoading && (
          <div className="text-center text-sm text-muted-foreground py-4">
            {t('common.showingOf', { current: contacts.length, total: totalCount, entity: t('contacts.title').toLowerCase() })}
          </div>
        )}
      </div>

      {/* Filters Dialog */}
      <ResponsiveDialog open={showFilters} onOpenChange={setShowFilters} title="Filtros">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sales Consultant Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.salesConsultant')}</Label>
              <MultiSelect
                options={[
                  { value: 'unassigned', label: t('common.unassigned') },
                  { value: 'me', label: t('contacts.assignedToMe') || 'Assigned to me' },
                  ...(appUsers?.map(user => ({
                    value: user.id,
                    label: user.display_name || `${user.first_name} ${user.last_name}`
                  })) || [])
                ]}
                value={salesConsultantFilter}
                onChange={setSalesConsultantFilter}
                placeholder={t('contacts.allSalesConsultants') || "All Sales Consultants"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Sales Development (SDR) Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.salesDevelopment')}</Label>
              <MultiSelect
                options={[
                  { value: 'unassigned', label: t('common.unassigned') },
                  { value: 'me', label: t('contacts.assignedToMe') || 'Assigned to me' },
                  ...(sdrs?.map(user => ({
                    value: user.id,
                    label: user.display_name || `${user.first_name} ${user.last_name}`
                  })) || [])
                ]}
                value={sdrFilter}
                onChange={setSdrFilter}
                placeholder={t('contacts.allSDRs') || "All SDRs"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Source Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.source')}</Label>
              <MultiSelect
                options={SOURCE_TYPES.map(source => ({
                  value: source.value,
                  label: t(source.labelKey)
                }))}
                value={sourceFilter}
                onChange={setSourceFilter}
                placeholder={t('contacts.allSources') || "All Sources"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Channel Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.channel')}</Label>
              <MultiSelect
                options={CHANNEL_TYPES.map(channel => ({
                  value: channel.value,
                  label: t(channel.labelKey)
                }))}
                value={channelFilter}
                onChange={setChannelFilter}
                placeholder={t('contacts.allChannels') || "All Channels"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Contact Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('contacts.contactType') || 'Contact Type'}</Label>
              <MultiSelect
                options={CONTACT_TYPES.map(ct => ({ value: ct.value, label: t(ct.labelKey) }))}
                value={contactTypeFilter}
                onChange={setContactTypeFilter}
                placeholder={t('contacts.allTypes') || "All Types"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Created By Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.createdBy')}</Label>
              <MultiSelect
                options={[
                  { value: 'unassigned', label: t('common.unknown') || 'Unknown' },
                  { value: 'me', label: t('contacts.createdByMe') || 'Created by me' },
                  ...(appUsers?.map(user => ({
                    value: user.id,
                    label: user.display_name || `${user.first_name} ${user.last_name}`
                  })) || [])
                ]}
                value={createdByFilter}
                onChange={setCreatedByFilter}
                placeholder={t('contacts.allUsers') || "All Users"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Deal Stage Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('contacts.dealStage') || 'Deal Stage'}</Label>
              <MultiSelect
                options={[
                  { value: 'no_deals', label: t('contacts.withoutDeals') || 'Without Deals' },
                  { value: 'any_deal', label: t('contacts.withAnyDeal') || 'With Any Deal' },
                  ...DEAL_STAGES.map(ds => ({ value: ds.value, label: t(ds.labelKey) }))
                ]}
                value={dealStageFilter}
                onChange={(values) => {
                  setDealStageFilter(values);
                  setFilterNoDeals(values.includes('no_deals'));
                }}
                placeholder={t('contacts.allStages') || "All Stages"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Verification Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('contacts.verificationStatus') || 'Verification Status'}</Label>
              <MultiSelect
                options={VERIFICATION_STATUSES.map(vs => ({ value: vs.value, label: t(vs.labelKey) }))}
                value={verificationFilter}
                onChange={setVerificationFilter}
                placeholder={t('contacts.allStatuses') || "All Statuses"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Created Date Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.createdAt')}</Label>
              <MultiSelect
                options={CREATED_DATE_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.labelKey) || opt.value }))}
                value={createdDateFilter}
                onChange={setCreatedDateFilter}
                placeholder={t('contacts.allDates') || "All Dates"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Business Unit Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('fields.businessUnit')}</Label>
              <MultiSelect
                options={BUSINESS_UNIT_OPTIONS.map(opt => ({ value: opt.value, label: opt.labelKey ? t(opt.labelKey) : opt.label }))}
                value={businessUnitFilter}
                onChange={setBusinessUnitFilter}
                placeholder={t('contacts.allBUs') || "All BUs"}
                emptyText={t('common.noResults')}
              />
            </div>

            {/* Reactivation Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('reactivation.status') || 'Reactivation Status'}</Label>
              <MultiSelect
                options={REACTIVATION_STATUS_OPTIONS.map(opt => ({ value: opt.value, label: t(opt.labelKey) || opt.value }))}
                value={reactivationStatusFilter}
                onChange={setReactivationStatusFilter}
                placeholder={t('reactivation.allStatuses') || "All Statuses"}
                emptyText={t('common.noResults')}
              />
            </div>
          </div>

          {/* Custom Date Range */}
          {createdDateFilter.includes('custom') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('dates.dateRange') || 'Date Range'}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <Calendar className="mr-2 h-4 w-4" />
                    {customDateRange?.from ? (
                      customDateRange.to ? (
                        <>
                          {format(customDateRange.from, dateFormats.short, { locale: dateLocale })} -{" "}
                          {format(customDateRange.to, dateFormats.short, { locale: dateLocale })}
                        </>
                      ) : (
                        format(customDateRange.from, dateFormats.short, { locale: dateLocale })
                      )
                    ) : (
                      <span>{t('dates.pickDateRange') || 'Pick a date range'}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={customDateRange?.from}
                    selected={customDateRange}
                    onSelect={setCustomDateRange}
                    numberOfMonths={2}
                    locale={dateLocale}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                handleClearAllFilters();
                toast.success(t('common.allFiltersRemoved'));
              }}
            >
              Limpar tudo
            </Button>
            <Button onClick={() => setShowFilters(false)}>
              Aplicar
            </Button>
          </div>
        </div>
      </ResponsiveDialog>

      {/* Contact Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Contact</DialogTitle>
          </DialogHeader>
          <ContactForm
            onSubmit={handleCreateContact}
            onCancel={() => setShowForm(false)}
            isProcessing={isProcessing}
            simplified={true}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar for contact list selection */}
      <BulkActionBar
        selectedCount={selectedContactIds.length}
        onClearSelection={clearContactSelection}
        onAction={() => setShowListModal(true)}
        itemType="contatos"
        actionLabel="Adicionar à Lista"
        options={[]}
        directActionTypes={['list']}
        actionType="list"
      />

      {/* List Modal */}
      <ListModal
        open={showListModal}
        onOpenChange={setShowListModal}
        contactIds={selectedContactIds}
        onSuccess={clearContactSelection}
      />

      {/* Merge Duplicates Dialog */}
      <MergeDuplicatesDialog
        open={showMergeDialog}
        onOpenChange={setShowMergeDialog}
        duplicates={duplicates}
        onMerge={async (keepId, mergeIds) => {
          await mergeContacts(keepId, mergeIds);
          if (loadContacts) await loadContacts();
        }}
        loading={mergeLoading}
      />
    </div>
  );
}
